/**
 * ClientPortal.jsx — Hiring Manager Client Portal
 *
 * Public page (no CRM auth) for hiring managers / clients to review
 * candidates submitted against their job orders.
 *
 * Access via token in URL: /client-portal?token={token}
 *
 * API endpoints (all append ?token={token}):
 *   GET  /api/ats/client-portal/dashboard
 *   GET  /api/ats/client-portal/jobs/{jobId}
 *   GET  /api/ats/client-portal/applicants/{applicantId}
 *   POST /api/ats/client-portal/applications/{appId}/feedback
 *   POST /api/ats/client-portal/applications/{appId}/shortlist
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
    Loader2, Briefcase, ArrowLeft, ChevronRight, ChevronDown, ChevronUp,
    Users, CheckCircle2, Star, ThumbsUp, ThumbsDown, Send, AlertCircle,
    Building2, User, Award, GraduationCap, Clock, Filter, SlidersHorizontal,
    X, Search, BookOpen, MapPin, Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEADING_FONT = 'Manrope, system-ui, sans-serif';

// ---------------------------------------------------------------------------
// Stage colours for badges
// ---------------------------------------------------------------------------

const STAGE_COLORS = {
    applied:    { bg: '#f5f5f4', text: '#78716c', dot: '#a8a29e' },
    screening:  { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
    shortlisted:{ bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
    interview:  { bg: '#ede9fe', text: '#5b21b6', dot: '#8b5cf6' },
    offered:    { bg: '#d1fae5', text: '#065f46', dot: '#10b981' },
    hired:      { bg: '#bbf7d0', text: '#14532d', dot: '#22c55e' },
    rejected:   { bg: '#fee2e2', text: '#991b1b', dot: '#ef4444' },
    withdrawn:  { bg: '#f5f5f4', text: '#78716c', dot: '#a8a29e' },
};

const DEFAULT_STAGE_COLOR = { bg: '#f5f5f4', text: '#57534e', dot: '#a8a29e' };

function getStageColor(stage) {
    if (!stage) return DEFAULT_STAGE_COLOR;
    const key = stage.toLowerCase().replace(/[\s_-]/g, '');
    for (const [k, v] of Object.entries(STAGE_COLORS)) {
        if (key.includes(k)) return v;
    }
    return DEFAULT_STAGE_COLOR;
}

// ---------------------------------------------------------------------------
// Status badge for jobs
// ---------------------------------------------------------------------------

const JOB_STATUS_STYLES = {
    open:   { bg: '#d1fae5', text: '#065f46' },
    closed: { bg: '#fee2e2', text: '#991b1b' },
    on_hold:{ bg: '#fef3c7', text: '#92400e' },
    draft:  { bg: '#f5f5f4', text: '#57534e' },
};

function StatusBadge({ status }) {
    const style = JOB_STATUS_STYLES[status] || JOB_STATUS_STYLES.draft;
    return (
        <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize"
            style={{ backgroundColor: style.bg, color: style.text }}
        >
            {(status || 'unknown').replace(/_/g, ' ')}
        </span>
    );
}

// ---------------------------------------------------------------------------
// Stage badge
// ---------------------------------------------------------------------------

function StageBadge({ stage, label }) {
    const color = getStageColor(stage);
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: color.bg, color: color.text }}
        >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color.dot }} />
            {label || stage || 'Unknown'}
        </span>
    );
}

// ---------------------------------------------------------------------------
// Match score bar
// ---------------------------------------------------------------------------

function MatchScoreBar({ score }) {
    if (score == null) return <span className="text-xs text-stone-400">N/A</span>;
    const pct = Math.round(score);
    const color = pct >= 80 ? '#16a34a' : pct >= 60 ? '#f59e0b' : pct >= 40 ? '#f97316' : '#ef4444';
    return (
        <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden" style={{ maxWidth: 80 }}>
                <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, backgroundColor: color }}
                />
            </div>
            <span className="text-xs font-medium tabular-nums" style={{ color }}>{pct}%</span>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Star rating input (1-5)
// ---------------------------------------------------------------------------

function StarRating({ value, onChange, readonly = false }) {
    const [hover, setHover] = useState(0);
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map(n => (
                <button
                    key={n}
                    type="button"
                    disabled={readonly}
                    onClick={() => !readonly && onChange(n)}
                    onMouseEnter={() => !readonly && setHover(n)}
                    onMouseLeave={() => !readonly && setHover(0)}
                    className={`p-0.5 transition-colors ${readonly ? 'cursor-default' : 'cursor-pointer'}`}
                >
                    <Star
                        className="w-5 h-5"
                        fill={(hover || value) >= n ? '#f59e0b' : 'none'}
                        stroke={(hover || value) >= n ? '#f59e0b' : '#d6d3d1'}
                        strokeWidth={1.5}
                    />
                </button>
            ))}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Slot progress bar for job cards
// ---------------------------------------------------------------------------

function SlotProgress({ filled, total }) {
    const pct = total > 0 ? Math.round((filled / total) * 100) : 0;
    return (
        <div className="w-full">
            <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
                <span>{filled} / {total} filled</span>
                <span>{pct}%</span>
            </div>
            <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                        width: `${pct}%`,
                        backgroundColor: pct >= 100 ? '#16a34a' : pct >= 50 ? '#3b82f6' : '#f59e0b',
                    }}
                />
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Mini stage breakdown dots for dashboard cards
// ---------------------------------------------------------------------------

function StageBreakdown({ byStage }) {
    if (!byStage || typeof byStage !== 'object') return null;
    const entries = Object.entries(byStage).filter(([, count]) => count > 0);
    if (!entries.length) return null;

    return (
        <div className="flex flex-wrap items-center gap-2 mt-2">
            {entries.map(([stage, count]) => {
                const color = getStageColor(stage);
                return (
                    <span key={stage} className="inline-flex items-center gap-1 text-xs text-stone-500">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color.dot }} />
                        <span className="capitalize">{stage.replace(/_/g, ' ')}</span>
                        <span className="font-medium text-stone-700">{count}</span>
                    </span>
                );
            })}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ClientPortal() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    // View state
    const [view, setView] = useState('dashboard'); // 'dashboard' | 'job' | 'applicant'
    const [selectedJobId, setSelectedJobId] = useState(null);
    const [selectedAppId, setSelectedAppId] = useState(null);
    const [selectedApplicantId, setSelectedApplicantId] = useState(null);

    // Data
    const [dashboard, setDashboard] = useState(null);
    const [jobDetail, setJobDetail] = useState(null);
    const [applicantProfile, setApplicantProfile] = useState(null);

    // Loading & errors
    const [loading, setLoading] = useState(true);
    const [jobLoading, setJobLoading] = useState(false);
    const [applicantLoading, setApplicantLoading] = useState(false);
    const [error, setError] = useState(null);

    // Job view filters
    const [stageFilter, setStageFilter] = useState('all');
    const [minScore, setMinScore] = useState(0);
    const [sortBy, setSortBy] = useState('match_score'); // 'match_score' | 'name' | 'stage' | 'created_at'
    const [sortDir, setSortDir] = useState('desc');
    const [showFilters, setShowFilters] = useState(false);
    const [descExpanded, setDescExpanded] = useState(false);

    // Feedback form
    const [feedbackText, setFeedbackText] = useState('');
    const [interested, setInterested] = useState(null);
    const [interviewScore, setInterviewScore] = useState(0);
    const [submittingFeedback, setSubmittingFeedback] = useState(false);
    const [feedbackSuccess, setFeedbackSuccess] = useState(false);
    const [shortlisting, setShortlisting] = useState(null); // appId being shortlisted

    // -----------------------------------------------------------------------
    // Fetch dashboard
    // -----------------------------------------------------------------------

    const fetchDashboard = useCallback(async () => {
        if (!token) { setError('No portal token provided'); setLoading(false); return; }
        try {
            setLoading(true);
            const res = await axios.get(`${API}/ats/client-portal/dashboard`, { params: { token } });
            setDashboard(res.data);
            setError(null);
        } catch (err) {
            setError(
                err.response?.status === 401 || err.response?.status === 403
                    ? 'Invalid or expired portal link'
                    : 'Unable to load portal. Please try again later.'
            );
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

    // -----------------------------------------------------------------------
    // Fetch job detail
    // -----------------------------------------------------------------------

    const openJob = useCallback(async (jobId) => {
        setSelectedJobId(jobId);
        setView('job');
        setJobDetail(null);
        setJobLoading(true);
        setStageFilter('all');
        setMinScore(0);
        setDescExpanded(false);
        try {
            const res = await axios.get(`${API}/ats/client-portal/jobs/${jobId}`, { params: { token } });
            setJobDetail(res.data);
        } catch {
            setJobDetail(null);
        } finally {
            setJobLoading(false);
        }
    }, [token]);

    // -----------------------------------------------------------------------
    // Fetch applicant profile
    // -----------------------------------------------------------------------

    const openApplicant = useCallback(async (applicantId, appId) => {
        setSelectedApplicantId(applicantId);
        setSelectedAppId(appId);
        setView('applicant');
        setApplicantProfile(null);
        setApplicantLoading(true);
        setFeedbackText('');
        setInterested(null);
        setInterviewScore(0);
        setFeedbackSuccess(false);
        try {
            const res = await axios.get(`${API}/ats/client-portal/applicants/${applicantId}`, { params: { token } });
            setApplicantProfile(res.data);
            // Pre-fill feedback if already submitted
            if (res.data.client_feedback) {
                setFeedbackText(res.data.client_feedback.feedback || '');
                setInterested(res.data.client_feedback.interested ?? null);
                setInterviewScore(res.data.client_feedback.interview_score || 0);
            }
        } catch {
            setApplicantProfile(null);
        } finally {
            setApplicantLoading(false);
        }
    }, [token]);

    // -----------------------------------------------------------------------
    // Submit feedback
    // -----------------------------------------------------------------------

    const submitFeedback = async () => {
        if (!selectedAppId || !token) return;
        setSubmittingFeedback(true);
        try {
            await axios.post(
                `${API}/ats/client-portal/applications/${selectedAppId}/feedback`,
                { feedback: feedbackText, interested, interview_score: interviewScore || null },
                { params: { token } }
            );
            setFeedbackSuccess(true);
            // Refresh the job detail so feedback snippet updates
            if (selectedJobId) {
                const res = await axios.get(`${API}/ats/client-portal/jobs/${selectedJobId}`, { params: { token } });
                setJobDetail(res.data);
            }
        } catch {
            // Silently handle — user can retry
        } finally {
            setSubmittingFeedback(false);
        }
    };

    // -----------------------------------------------------------------------
    // Shortlist candidate
    // -----------------------------------------------------------------------

    const shortlistCandidate = async (appId) => {
        if (!token) return;
        setShortlisting(appId);
        try {
            await axios.post(
                `${API}/ats/client-portal/applications/${appId}/shortlist`,
                {},
                { params: { token } }
            );
            // Refresh job detail
            if (selectedJobId) {
                const res = await axios.get(`${API}/ats/client-portal/jobs/${selectedJobId}`, { params: { token } });
                setJobDetail(res.data);
            }
        } catch {
            // Silently handle
        } finally {
            setShortlisting(null);
        }
    };

    // -----------------------------------------------------------------------
    // Navigation
    // -----------------------------------------------------------------------

    const goBack = () => {
        if (view === 'applicant') {
            setView('job');
            setSelectedApplicantId(null);
            setSelectedAppId(null);
            setApplicantProfile(null);
        } else if (view === 'job') {
            setView('dashboard');
            setSelectedJobId(null);
            setJobDetail(null);
        }
    };

    // -----------------------------------------------------------------------
    // Filtered & sorted applications
    // -----------------------------------------------------------------------

    const filteredApplications = useMemo(() => {
        if (!jobDetail?.applications) return [];
        let apps = [...jobDetail.applications];

        // Stage filter
        if (stageFilter !== 'all') {
            apps = apps.filter(a => a.stage === stageFilter);
        }

        // Score filter
        if (minScore > 0) {
            apps = apps.filter(a => (a.ai_match_score || 0) >= minScore);
        }

        // Sort
        apps.sort((a, b) => {
            let cmp = 0;
            switch (sortBy) {
                case 'match_score':
                    cmp = (a.ai_match_score || 0) - (b.ai_match_score || 0);
                    break;
                case 'name':
                    cmp = (a.applicant_name || '').localeCompare(b.applicant_name || '');
                    break;
                case 'stage':
                    cmp = (a.stage || '').localeCompare(b.stage || '');
                    break;
                case 'created_at':
                    cmp = new Date(a.created_at || 0) - new Date(b.created_at || 0);
                    break;
                default:
                    break;
            }
            return sortDir === 'desc' ? -cmp : cmp;
        });

        return apps;
    }, [jobDetail, stageFilter, minScore, sortBy, sortDir]);

    // Unique stages for filter dropdown
    const availableStages = useMemo(() => {
        if (!jobDetail?.applications) return [];
        const stages = [...new Set(jobDetail.applications.map(a => a.stage).filter(Boolean))];
        return stages.sort();
    }, [jobDetail]);

    // -----------------------------------------------------------------------
    // Render: Loading
    // -----------------------------------------------------------------------

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAF5' }}>
                <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-stone-400 mx-auto mb-3" />
                    <p className="text-sm text-stone-500">Loading portal...</p>
                </div>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Render: Error
    // -----------------------------------------------------------------------

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAF5' }}>
                <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 max-w-md mx-4 text-center">
                    <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <h2
                        className="text-lg font-semibold text-stone-900 mb-2"
                        style={{ fontFamily: HEADING_FONT }}
                    >
                        Access Denied
                    </h2>
                    <p className="text-stone-500 text-sm">{error}</p>
                    <p className="text-stone-400 text-xs mt-4">
                        If you believe this is an error, please contact your recruiter for a new link.
                    </p>
                </div>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Render: Dashboard
    // -----------------------------------------------------------------------

    if (view === 'dashboard') {
        return (
            <div className="min-h-screen" style={{ backgroundColor: '#FAFAF5' }}>
                {/* Header */}
                <div className="bg-stone-900 text-white">
                    <div className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
                        <div className="flex items-center gap-3 mb-2">
                            <Building2 className="w-5 h-5 text-stone-400" />
                            <span className="text-sm text-stone-400">{dashboard?.account_name}</span>
                        </div>
                        <h1
                            className="text-2xl sm:text-3xl font-bold"
                            style={{ fontFamily: HEADING_FONT }}
                        >
                            Welcome, {dashboard?.contact_name}
                        </h1>
                        <p className="text-stone-400 mt-2 text-sm">
                            Review candidates and provide feedback on your open positions.
                        </p>
                    </div>
                </div>

                {/* Job cards */}
                <div className="max-w-5xl mx-auto px-6 py-8">
                    <div className="flex items-center justify-between mb-6">
                        <h2
                            className="text-lg font-semibold text-stone-900"
                            style={{ fontFamily: HEADING_FONT }}
                        >
                            Your Job Orders
                        </h2>
                        <span className="text-sm text-stone-500">
                            {dashboard?.jobs?.length || 0} position{(dashboard?.jobs?.length || 0) !== 1 ? 's' : ''}
                        </span>
                    </div>

                    {!dashboard?.jobs?.length ? (
                        <div className="text-center py-16">
                            <Briefcase className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                            <p className="text-stone-500">No job orders found.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {dashboard.jobs.map(job => (
                                <button
                                    key={job.job_id}
                                    onClick={() => openJob(job.job_id)}
                                    className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md hover:border-stone-300 transition-all text-left group w-full"
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex-1 min-w-0">
                                            <h3
                                                className="font-semibold text-stone-900 group-hover:text-stone-700 transition-colors truncate"
                                                style={{ fontFamily: HEADING_FONT }}
                                            >
                                                {job.title}
                                            </h3>
                                        </div>
                                        <div className="ml-3 flex items-center gap-2">
                                            <StatusBadge status={job.status} />
                                            <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-stone-500 transition-colors" />
                                        </div>
                                    </div>

                                    <SlotProgress filled={job.filled_slots || 0} total={job.slots || 1} />

                                    <div className="flex items-center gap-4 mt-3 text-xs text-stone-500">
                                        <span className="flex items-center gap-1">
                                            <Users className="w-3.5 h-3.5" />
                                            {job.total_applicants || 0} candidate{(job.total_applicants || 0) !== 1 ? 's' : ''}
                                        </span>
                                    </div>

                                    <StageBreakdown byStage={job.by_stage} />
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Footer */}
                    <div className="text-center mt-16 py-6 border-t border-stone-200">
                        <p className="text-xs text-stone-400">Powered by NurtureCRM</p>
                    </div>
                </div>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Render: Job Detail
    // -----------------------------------------------------------------------

    if (view === 'job') {
        return (
            <div className="min-h-screen" style={{ backgroundColor: '#FAFAF5' }}>
                {/* Top bar */}
                <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
                    <div className="max-w-5xl mx-auto px-6 py-3 flex items-center gap-3">
                        <button
                            onClick={goBack}
                            className="flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900 transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Dashboard
                        </button>
                    </div>
                </div>

                <div className="max-w-5xl mx-auto px-6 py-8">
                    {jobLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                        </div>
                    ) : !jobDetail ? (
                        <div className="text-center py-20">
                            <AlertCircle className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                            <p className="text-stone-500">Unable to load job details.</p>
                        </div>
                    ) : (
                        <>
                            {/* Job header */}
                            <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6">
                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-1">
                                            <h1
                                                className="text-xl sm:text-2xl font-bold text-stone-900"
                                                style={{ fontFamily: HEADING_FONT }}
                                            >
                                                {jobDetail.job.title}
                                            </h1>
                                            <StatusBadge status={jobDetail.job.status} />
                                        </div>
                                        <div className="flex items-center gap-4 text-sm text-stone-500 mt-2">
                                            <span>{jobDetail.job.filled_slots || 0} / {jobDetail.job.slots || 0} slots filled</span>
                                            <span>{jobDetail.applications?.length || 0} candidate{(jobDetail.applications?.length || 0) !== 1 ? 's' : ''}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Skills */}
                                {jobDetail.job.required_skills?.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-4">
                                        {jobDetail.job.required_skills.map(skill => (
                                            <span
                                                key={skill}
                                                className="inline-flex items-center px-2.5 py-1 rounded-lg bg-stone-100 text-xs font-medium text-stone-600"
                                            >
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Description (collapsible) */}
                                {jobDetail.job.description && (
                                    <div className="mt-4">
                                        <button
                                            onClick={() => setDescExpanded(!descExpanded)}
                                            className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 transition-colors"
                                        >
                                            <BookOpen className="w-4 h-4" />
                                            Job Description
                                            {descExpanded
                                                ? <ChevronUp className="w-3.5 h-3.5" />
                                                : <ChevronDown className="w-3.5 h-3.5" />
                                            }
                                        </button>
                                        {descExpanded && (
                                            <div className="mt-3 text-sm text-stone-600 leading-relaxed whitespace-pre-wrap bg-stone-50 rounded-lg p-4 border border-stone-100">
                                                {jobDetail.job.description}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Filters */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                                <h2
                                    className="text-base font-semibold text-stone-900"
                                    style={{ fontFamily: HEADING_FONT }}
                                >
                                    Candidates ({filteredApplications.length})
                                </h2>
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border transition-colors ${
                                        showFilters
                                            ? 'bg-stone-900 text-white border-stone-900'
                                            : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                                    }`}
                                >
                                    <SlidersHorizontal className="w-3.5 h-3.5" />
                                    Filters
                                </button>
                            </div>

                            {showFilters && (
                                <div className="bg-white rounded-xl border border-stone-200 p-4 mb-4 flex flex-wrap items-end gap-4">
                                    {/* Stage filter */}
                                    <div>
                                        <label className="block text-xs text-stone-500 mb-1">Stage</label>
                                        <select
                                            value={stageFilter}
                                            onChange={e => setStageFilter(e.target.value)}
                                            className="text-sm border border-stone-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                                        >
                                            <option value="all">All Stages</option>
                                            {availableStages.map(s => (
                                                <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Min score */}
                                    <div>
                                        <label className="block text-xs text-stone-500 mb-1">Min Match Score</label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={10}
                                            value={minScore}
                                            onChange={e => setMinScore(Number(e.target.value) || 0)}
                                            className="text-sm border border-stone-200 rounded-lg px-3 py-1.5 w-20 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                                        />
                                    </div>

                                    {/* Sort */}
                                    <div>
                                        <label className="block text-xs text-stone-500 mb-1">Sort by</label>
                                        <select
                                            value={sortBy}
                                            onChange={e => setSortBy(e.target.value)}
                                            className="text-sm border border-stone-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                                        >
                                            <option value="match_score">Match Score</option>
                                            <option value="name">Name</option>
                                            <option value="stage">Stage</option>
                                            <option value="created_at">Date Applied</option>
                                        </select>
                                    </div>

                                    {/* Sort dir */}
                                    <button
                                        onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                                        className="text-sm border border-stone-200 rounded-lg px-3 py-1.5 bg-white hover:bg-stone-50 transition-colors"
                                    >
                                        {sortDir === 'desc' ? 'Descending' : 'Ascending'}
                                    </button>

                                    {/* Clear */}
                                    {(stageFilter !== 'all' || minScore > 0) && (
                                        <button
                                            onClick={() => { setStageFilter('all'); setMinScore(0); }}
                                            className="text-sm text-stone-500 hover:text-stone-700 flex items-center gap-1"
                                        >
                                            <X className="w-3.5 h-3.5" /> Clear
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Candidate cards */}
                            {filteredApplications.length === 0 ? (
                                <div className="text-center py-16">
                                    <Users className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                                    <p className="text-stone-500 text-sm">
                                        {jobDetail.applications?.length ? 'No candidates match your filters.' : 'No candidates submitted yet.'}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {filteredApplications.map(app => (
                                        <div
                                            key={app.application_id}
                                            className="bg-white rounded-xl border border-stone-200 p-4 sm:p-5 hover:shadow-sm transition-shadow"
                                        >
                                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                                {/* Left: name + stage */}
                                                <div className="flex-1 min-w-0">
                                                    <button
                                                        onClick={() => openApplicant(app.application_id, app.application_id)}
                                                        className="text-sm font-semibold text-stone-900 hover:text-blue-700 transition-colors text-left"
                                                        style={{ fontFamily: HEADING_FONT }}
                                                    >
                                                        {app.applicant_name || 'Unnamed Candidate'}
                                                    </button>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <StageBadge stage={app.stage} label={app.stage_label} />
                                                        {app.created_at && (
                                                            <span className="text-xs text-stone-400 flex items-center gap-1">
                                                                <Clock className="w-3 h-3" />
                                                                {new Date(app.created_at).toLocaleDateString()}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Middle: match score */}
                                                <div className="sm:w-36">
                                                    <span className="text-xs text-stone-400 block mb-1">Match Score</span>
                                                    <MatchScoreBar score={app.ai_match_score} />
                                                </div>

                                                {/* Feedback snippet */}
                                                {app.client_feedback && (
                                                    <div className="sm:w-40">
                                                        <span className="text-xs text-stone-400 block mb-1">Your Feedback</span>
                                                        <p className="text-xs text-stone-600 line-clamp-2">
                                                            {typeof app.client_feedback === 'string'
                                                                ? app.client_feedback
                                                                : app.client_feedback.feedback || 'Submitted'
                                                            }
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Actions */}
                                                <div className="flex items-center gap-2 sm:ml-2">
                                                    <button
                                                        onClick={() => openApplicant(app.application_id, app.application_id)}
                                                        className="text-xs px-3 py-1.5 rounded-lg bg-stone-900 text-white hover:bg-stone-800 transition-colors"
                                                    >
                                                        Review Profile
                                                    </button>
                                                    <button
                                                        onClick={() => shortlistCandidate(app.application_id)}
                                                        disabled={shortlisting === app.application_id}
                                                        className="text-xs px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                                                    >
                                                        {shortlisting === app.application_id
                                                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                            : 'Shortlist'
                                                        }
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {/* Footer */}
                    <div className="text-center mt-16 py-6 border-t border-stone-200">
                        <p className="text-xs text-stone-400">Powered by NurtureCRM</p>
                    </div>
                </div>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Render: Applicant Profile
    // -----------------------------------------------------------------------

    if (view === 'applicant') {
        return (
            <div className="min-h-screen" style={{ backgroundColor: '#FAFAF5' }}>
                {/* Top bar */}
                <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
                    <div className="max-w-5xl mx-auto px-6 py-3 flex items-center gap-3">
                        <button
                            onClick={goBack}
                            className="flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900 transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Candidates
                        </button>
                    </div>
                </div>

                <div className="max-w-5xl mx-auto px-6 py-8">
                    {applicantLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                        </div>
                    ) : !applicantProfile ? (
                        <div className="text-center py-20">
                            <AlertCircle className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                            <p className="text-stone-500">Unable to load applicant profile.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Left column: profile */}
                            <div className="lg:col-span-2 space-y-6">
                                {/* Profile header */}
                                <div className="bg-white rounded-xl border border-stone-200 p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="w-14 h-14 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                                            <User className="w-7 h-7 text-stone-400" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h1
                                                className="text-xl font-bold text-stone-900"
                                                style={{ fontFamily: HEADING_FONT }}
                                            >
                                                {applicantProfile.name || 'Unnamed Candidate'}
                                            </h1>
                                            {applicantProfile.headline && (
                                                <p className="text-sm text-stone-600 mt-0.5">{applicantProfile.headline}</p>
                                            )}
                                            {applicantProfile.location && (
                                                <p className="text-xs text-stone-400 mt-1 flex items-center gap-1">
                                                    <MapPin className="w-3 h-3" />
                                                    {applicantProfile.location}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Match score */}
                                    {applicantProfile.match_score != null && (
                                        <div className="mt-5 p-4 rounded-lg bg-stone-50 border border-stone-100">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-medium text-stone-700 flex items-center gap-1.5">
                                                    <Sparkles className="w-4 h-4 text-amber-500" />
                                                    AI Match Score
                                                </span>
                                                <span className="text-lg font-bold text-stone-900">
                                                    {Math.round(applicantProfile.match_score)}%
                                                </span>
                                            </div>
                                            <div className="h-2.5 bg-stone-200 rounded-full overflow-hidden mb-3">
                                                <div
                                                    className="h-full rounded-full transition-all duration-700"
                                                    style={{
                                                        width: `${applicantProfile.match_score}%`,
                                                        backgroundColor: applicantProfile.match_score >= 80
                                                            ? '#16a34a'
                                                            : applicantProfile.match_score >= 60
                                                                ? '#f59e0b'
                                                                : '#ef4444',
                                                    }}
                                                />
                                            </div>
                                            {applicantProfile.match_reasoning && (
                                                <p className="text-xs text-stone-500 leading-relaxed">
                                                    {applicantProfile.match_reasoning}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Summary */}
                                {applicantProfile.summary && (
                                    <div className="bg-white rounded-xl border border-stone-200 p-6">
                                        <h2
                                            className="text-sm font-semibold text-stone-900 mb-3"
                                            style={{ fontFamily: HEADING_FONT }}
                                        >
                                            Summary
                                        </h2>
                                        <p className="text-sm text-stone-600 leading-relaxed whitespace-pre-wrap">
                                            {applicantProfile.summary}
                                        </p>
                                    </div>
                                )}

                                {/* Work history */}
                                {applicantProfile.work_history?.length > 0 && (
                                    <div className="bg-white rounded-xl border border-stone-200 p-6">
                                        <h2
                                            className="text-sm font-semibold text-stone-900 mb-4 flex items-center gap-2"
                                            style={{ fontFamily: HEADING_FONT }}
                                        >
                                            <Briefcase className="w-4 h-4 text-stone-400" />
                                            Work Experience
                                        </h2>
                                        <div className="space-y-4">
                                            {applicantProfile.work_history.map((job, i) => (
                                                <div key={i} className="relative pl-6 border-l-2 border-stone-200 pb-4 last:pb-0">
                                                    <div className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-stone-400" />
                                                    <h3 className="text-sm font-medium text-stone-900">
                                                        {job.title || job.role}
                                                    </h3>
                                                    <p className="text-xs text-stone-500">
                                                        {job.company}
                                                        {job.start_date && (
                                                            <span className="ml-2 text-stone-400">
                                                                {job.start_date}{job.end_date ? ` - ${job.end_date}` : ' - Present'}
                                                            </span>
                                                        )}
                                                    </p>
                                                    {job.description && (
                                                        <p className="text-xs text-stone-500 mt-1 leading-relaxed">
                                                            {job.description}
                                                        </p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Skills */}
                                {applicantProfile.skills?.length > 0 && (
                                    <div className="bg-white rounded-xl border border-stone-200 p-6">
                                        <h2
                                            className="text-sm font-semibold text-stone-900 mb-3 flex items-center gap-2"
                                            style={{ fontFamily: HEADING_FONT }}
                                        >
                                            <Award className="w-4 h-4 text-stone-400" />
                                            Skills
                                        </h2>
                                        <div className="flex flex-wrap gap-2">
                                            {applicantProfile.skills.map((skill, i) => {
                                                const label = typeof skill === 'string' ? skill : skill.name;
                                                const proficiency = typeof skill === 'object' ? skill.proficiency : null;
                                                return (
                                                    <span
                                                        key={i}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-50 border border-stone-200 text-xs font-medium text-stone-700"
                                                    >
                                                        {label}
                                                        {proficiency && (
                                                            <span className="text-stone-400 font-normal">
                                                                ({proficiency})
                                                            </span>
                                                        )}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Education */}
                                {applicantProfile.education?.length > 0 && (
                                    <div className="bg-white rounded-xl border border-stone-200 p-6">
                                        <h2
                                            className="text-sm font-semibold text-stone-900 mb-4 flex items-center gap-2"
                                            style={{ fontFamily: HEADING_FONT }}
                                        >
                                            <GraduationCap className="w-4 h-4 text-stone-400" />
                                            Education
                                        </h2>
                                        <div className="space-y-3">
                                            {applicantProfile.education.map((edu, i) => (
                                                <div key={i} className="flex items-start gap-3">
                                                    <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                        <GraduationCap className="w-4 h-4 text-stone-400" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-stone-900">
                                                            {edu.degree || edu.qualification}
                                                        </p>
                                                        <p className="text-xs text-stone-500">
                                                            {edu.institution || edu.school}
                                                            {edu.year && <span className="ml-2 text-stone-400">{edu.year}</span>}
                                                            {edu.graduation_year && <span className="ml-2 text-stone-400">{edu.graduation_year}</span>}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Right column: feedback form */}
                            <div className="space-y-6">
                                <div className="bg-white rounded-xl border border-stone-200 p-6 sticky top-16">
                                    <h2
                                        className="text-sm font-semibold text-stone-900 mb-4"
                                        style={{ fontFamily: HEADING_FONT }}
                                    >
                                        Your Feedback
                                    </h2>

                                    {feedbackSuccess ? (
                                        <div className="text-center py-6">
                                            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                                            <p className="text-sm font-medium text-stone-900">Feedback Submitted</p>
                                            <p className="text-xs text-stone-500 mt-1">Thank you for your review.</p>
                                            <button
                                                onClick={() => setFeedbackSuccess(false)}
                                                className="text-xs text-blue-600 hover:text-blue-700 mt-3"
                                            >
                                                Edit Feedback
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Interested toggle */}
                                            <div className="mb-4">
                                                <label className="block text-xs text-stone-500 mb-2">Interested in this candidate?</label>
                                                <div className="flex gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => setInterested(true)}
                                                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-colors ${
                                                            interested === true
                                                                ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                                                                : 'bg-white border-stone-200 text-stone-500 hover:border-stone-300'
                                                        }`}
                                                    >
                                                        <ThumbsUp className="w-4 h-4" />
                                                        Yes
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setInterested(false)}
                                                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-colors ${
                                                            interested === false
                                                                ? 'bg-red-50 border-red-300 text-red-700'
                                                                : 'bg-white border-stone-200 text-stone-500 hover:border-stone-300'
                                                        }`}
                                                    >
                                                        <ThumbsDown className="w-4 h-4" />
                                                        No
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Interview score */}
                                            <div className="mb-4">
                                                <label className="block text-xs text-stone-500 mb-2">Interview Score (optional)</label>
                                                <StarRating value={interviewScore} onChange={setInterviewScore} />
                                            </div>

                                            {/* Feedback text */}
                                            <div className="mb-4">
                                                <label className="block text-xs text-stone-500 mb-2">Comments</label>
                                                <textarea
                                                    value={feedbackText}
                                                    onChange={e => setFeedbackText(e.target.value)}
                                                    placeholder="Share your thoughts on this candidate..."
                                                    rows={5}
                                                    className="w-full text-sm border border-stone-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/10 resize-none"
                                                />
                                            </div>

                                            {/* Submit */}
                                            <button
                                                onClick={submitFeedback}
                                                disabled={submittingFeedback || (!feedbackText.trim() && interested === null)}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-stone-900 text-white text-sm font-medium hover:bg-stone-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {submittingFeedback ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <>
                                                        <Send className="w-4 h-4" />
                                                        Submit Feedback
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    )}
                                </div>

                                {/* Quick actions */}
                                {selectedAppId && (
                                    <div className="bg-white rounded-xl border border-stone-200 p-6">
                                        <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Quick Actions</h3>
                                        <div className="space-y-2">
                                            <button
                                                onClick={() => shortlistCandidate(selectedAppId)}
                                                disabled={shortlisting === selectedAppId}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm font-medium hover:bg-emerald-100 transition-colors disabled:opacity-50"
                                            >
                                                {shortlisting === selectedAppId ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <>
                                                        <CheckCircle2 className="w-4 h-4" />
                                                        Shortlist Candidate
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Footer */}
                    <div className="text-center mt-16 py-6 border-t border-stone-200">
                        <p className="text-xs text-stone-400">Powered by NurtureCRM</p>
                    </div>
                </div>
            </div>
        );
    }

    return null;
}
